import React, { Component } from 'react';
import { connect } from "react-redux";
import { withRouter } from "react-router-dom";

import View from './mfa_view';
import { oauth2_mfa, resendMfaCode, checkMFASession, debugCache } from "../../APIs";
import { getUrlVars } from "../../utils";
import { getAuthParams } from '../signin/utils';
import { isUSezpayRequest } from '../../utils/domainDetection';

class MFA extends Component {
    constructor(props) {
        super(props);
        this.state = {
            authentication_code: "",
            loading: false,
            resendLoading: false,
            error: null,
            sessionStatus: null
        };

        this.mfaFieldsInfo = [
            {
                title: "Verification Code",
                field: "authentication_code"
            }
        ];

        this.mfaHandler = this.mfaHandler.bind(this);
        this.setInputField = this.setInputField.bind(this);
        this.loadingToggler = this.loadingToggler.bind(this);
        this.resendHandler = this.resendHandler.bind(this);
    }

    setInputField = field => {
        return event => {
            this.setState({ [field]: event.target.value });
        }
    };

    loadingToggler() {
        this.setState({
            loading: !this.state.loading
        })
    }

    resendHandler() {
        this.setState({ resendLoading: true, error: null });

        console.log("🔧 FRONTEND MFA - Resend: Using session synchronization resend");

        // Get OAuth2 parameters for resend
        const { base_url, redirect_uri, code_challenge, code_challenge_method, scope } = getAuthParams();
        const validScope = scope && scope !== "undefined" ? scope : null;

        // CRITICAL FIX: Use base_url to match login flow (same as MFA verification)
        // Use the session synchronization resend endpoint
        resendMfaCode(base_url, code_challenge, code_challenge_method, validScope)
        .then(res => {
            console.log("🔧 FRONTEND MFA - Session synchronization resend successful:", res.data);
            
            // Handle session synchronization response
            if (res.data.success && res.data.sessionSynchronized) {
                console.log("🔧 FRONTEND MFA - Session synchronized successfully");
                console.log("🔧 FRONTEND MFA - New CSRF token:", res.data.newCsrfToken);
                console.log("🔧 FRONTEND MFA - Session key:", res.data.sessionKey);
                
                // CRITICAL STEP: Update the CSRF token in the URL for session synchronization
                if (res.data.newCsrfToken) {
                    const currentUrl = new URL(window.location.href);
                    currentUrl.searchParams.set('csrf_token', res.data.newCsrfToken);
                    window.history.replaceState({}, '', currentUrl.toString());
                    
                    console.log("🔧 FRONTEND MFA - Updated CSRF token in URL for session synchronization");
                    
                    // IMPORTANT: Store the new CSRF token for MFA verification
                    // This ensures MFA verification uses the same session as resend
                    localStorage.setItem('current_csrf_token', res.data.newCsrfToken);
                    sessionStorage.setItem('current_csrf_token', res.data.newCsrfToken);
                    
                    console.log("🔧 FRONTEND MFA - Stored new CSRF token for MFA verification");
                }
                
                this.setState({ 
                    resendLoading: false,
                    error: null
                });
            } else {
                console.log("🔧 FRONTEND MFA - Standard resend successful");
                this.setState({ 
                    resendLoading: false,
                    error: null
                });
            }
        })
        .catch(err => {
            console.error("🔧 FRONTEND MFA - Session synchronization resend failed:", err);
            this.setState({ 
                resendLoading: false, 
                error: err.response?.data?.message || "Failed to resend SMS. Please try again." 
            });
        });
    }

    mfaHandler() {
        this.loadingToggler();
        this.setState({ error: null });
        
        const { authentication_code } = this.state;
        const { base_url, redirect_uri, code_challenge, code_challenge_method, scope } = getAuthParams();
        const urlParams = getUrlVars();
        
        // Only pass scope if it has valid values
        const validScope = scope && scope !== "undefined" ? scope : null;
        
        // SESSION SYNCHRONIZATION: Get the synchronized CSRF token
        const synchronizedCsrfToken = localStorage.getItem('current_csrf_token') || 
                                     sessionStorage.getItem('current_csrf_token') || 
                                     urlParams.csrf_token;
        
        console.log("🔧 FRONTEND MFA - Session synchronization check:");
        console.log("🔧 FRONTEND MFA - Synchronized CSRF token:", synchronizedCsrfToken);
        console.log("🔧 FRONTEND MFA - URL CSRF token:", urlParams.csrf_token);
        
        console.log("🔧 FRONTEND MFA - Parameters being sent:", {
            base_url,
            redirect_uri,
            code_challenge,
            code_challenge_method,
            authentication_code,
            validScope,
            synchronizedCsrfToken
        });

        // Log cookies for debugging
        console.log("🔧 FRONTEND MFA - All cookies:", document.cookie);

        // CRITICAL FIX: Use base_url (not redirect_uri) to match login flow
        // AWS Cognito requires same redirect_uri for login and MFA
        // Frontend will manually redirect to full redirect_uri after getting code (line 164)
        oauth2_mfa(base_url, code_challenge, code_challenge_method, authentication_code, validScope)
        .then(res => {
            this.onMFASuccess(res.data);
        })
        .catch(err => {
            this.onMFAError(err);
            this.loadingToggler();
        })
    }

    onMFASuccess = (data) => {
        console.log("🔧 FRONTEND MFA - MFA verification successful:", data);
        
        // Handle OAuth2 response format
        if (data.code) {
            // Get the original redirect_uri from the URL (the full path)
            const urlParams = getUrlVars();
            const originalRedirectUri = urlParams.redirect_uri ? decodeURIComponent(urlParams.redirect_uri) : null;
            
            // If we don't have the original redirect_uri, use the one from getAuthParams (which is now base_url)
            const finalRedirectUri = originalRedirectUri || getAuthParams().redirect_uri;
            
            console.log("🔧 FRONTEND MFA - Original redirect_uri:", originalRedirectUri);
            console.log("🔧 FRONTEND MFA - Final redirect_uri:", finalRedirectUri);
            
            if (finalRedirectUri) {
                try {
                    const finalUrl = this.insertCodeQueryParams(finalRedirectUri, data.code);
                    console.log("🔧 FRONTEND MFA - Final redirect URL:", finalUrl);
                    window.location.href = finalUrl;
                } catch (urlError) {
                    console.error("🔧 FRONTEND MFA - URL construction failed:", urlError);
                    this.setState({ 
                        error: "Failed to redirect after successful MFA verification. Please try again." 
                    });
                    this.loadingToggler();
                }
            } else {
                this.setState({ error: "Missing redirect_uri parameter" });
                this.loadingToggler();
            }
        } else {
            console.error("🔧 FRONTEND MFA - No authorization code in response:", data);
            this.setState({ error: "Invalid response from server - no authorization code" });
            this.loadingToggler();
        }
    }

    insertCodeQueryParams(urlStr, code) {
        let url = new URL(urlStr);
        let params = url.searchParams;
        params.append('code', code);
        return url.toString()
    }

    onMFAError = (err) => {
        console.error("🔧 FRONTEND MFA - MFA Error:", err);
        
        // Handle different error types without showing browser alerts
        let errorMessage = "The code is incorrect, please try again";
        
        if (err.response && err.response.data && err.response.data.message) {
            errorMessage = err.response.data.message;
        } else if (err.response && err.response.data && err.response.data.error) {
            errorMessage = err.response.data.error;
        } else if (err.message) {
            errorMessage = err.message;
        }
        
        this.setState({ error: errorMessage });
    }

    componentDidMount() {
        // Check MFA session status on page load for consistency
        this.checkSessionStatus();

        // Debug cache on page load
        this.debugCacheStatus();

        // Check if we have the required parameters
        const urlParams = getUrlVars();
        if (!urlParams.redirect_uri) {
            this.setState({ error: "Missing redirect_uri parameter" });
            return;
        }
    }

    // Check MFA session status for page refresh consistency
    checkSessionStatus() {
        checkMFASession()
        .then(res => {
            console.log("🔧 FRONTEND MFA - Session status:", res.data);
            this.setState({ sessionStatus: res.data });
            
            if (res.data.hasCredentials) {
                console.log("🔧 FRONTEND MFA - Cached credentials found for:", res.data.username);
            } else {
                console.log("🔧 FRONTEND MFA - No cached credentials found");
            }
        })
        .catch(err => {
            console.log("🔧 FRONTEND MFA - Session check failed:", err);
            this.setState({ sessionStatus: { hasCredentials: false } });
        });
    }

    // Debug cache status
    debugCacheStatus() {
        debugCache()
        .then(res => {
            console.log("🔧 FRONTEND MFA - Cache debug info:", res.data);
        })
        .catch(err => {
            console.log("🔧 FRONTEND MFA - Cache debug failed:", err);
        });
    }

    render() {
        const isUSezpay = isUSezpayRequest();
        return (
            <View
                mfaFieldsInfo={this.mfaFieldsInfo}
                mfaHandler={this.mfaHandler}
                setInputField={this.setInputField}
                loading={this.state.loading}
                resendLoading={this.state.resendLoading}
                resendHandler={this.resendHandler}
                state={this.state}
                error={this.state.error}
                messagePool={this.props.messagePool}
                isUSezpay={isUSezpay}
            />
        );
    }
}

function mapStateToProps(state){
    return {
        messagePool: state.messages
    }
}

export default connect(mapStateToProps)(withRouter(MFA)); 